import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Inbox } from 'lucide-react';
import { Badge } from './ui/badge';

/**
 * MessagesIconButton — Iter-268
 * Renders an inbox icon for the top nav with an unread badge that
 * auto-refreshes every 60 s. Clicking navigates to the role-appropriate
 * messages page.
 *
 * Props:
 *   targetPath  — override the route (default '/talent/messages-center')
 *   className   — extra classes for the button wrapper
 */
const MessagesIconButton = ({ targetPath = '/talent/messages-center', unreadSource = '/messages/summary', className = '' }) => {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [urgent, setUrgent] = useState(0);

  const refresh = async () => {
    try {
      const r = await api.get(unreadSource);
      setUnread(r.data?.total_unread || 0);
      setUrgent(r.data?.total_urgent || 0);
    } catch (_) { /* silent */ }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60_000);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [unreadSource]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <button
      type="button"
      onClick={() => navigate(targetPath)}
      className={`relative inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-slate-100 transition-colors ${className}`}
      data-testid="messages-icon-btn"
      aria-label={`Messages${unread > 0 ? ` (${unread} unread)` : ''}`}
    >
      <Inbox className="w-5 h-5 text-slate-700" />
      {unread > 0 && (
        <Badge
          className={`absolute -top-1 -right-1 ${
            urgent > 0
              ? 'bg-rose-600 text-white border-2 border-white'
              : 'bg-cyan-600 text-white border-2 border-white'
          } text-[9px] px-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full`}
          data-testid="messages-unread-badge"
        >
          {unread > 99 ? '99+' : unread}
        </Badge>
      )}
    </button>
  );
};

export default MessagesIconButton;
